import React, { useEffect, useMemo, useState, useRef, createElement } from 'react';
import { X, ExternalLink, Settings } from 'lucide-react';
/* =========================================================================
   TYPES
   ========================================================================= */
type AgentId = 'discovery' | 'extraction' | 'validation' | 'underwriting' | 'audit';
type AgentSummary = {
  id: AgentId;
  name: string;
  ips: number;
  processed: number;
  confidence?: number;
};
type AgentDetail = {
  id: 'discovery';
  sources: string[];
  query: string;
  indexed: number;
} | {
  id: 'extraction';
  fieldParity: number;
  duplicatesResolved: number;
  schemaVersion: string;
} | {
  id: 'validation';
  evidence: {
    erp: boolean;
    buyer: boolean;
    ship: boolean;
    bank: boolean;
    hash: boolean;
  };
  buyerMismatch: number;
  erpFail: number;
} | {
  id: 'underwriting';
  scoreFormula: string;
  threshold: number;
  above085: number;
} | {
  id: 'audit';
  verifications24h: number;
  fingerprint: string;
  explorerUrl: string;
};
/* =========================================================================
   SEED DATA
   ========================================================================= */
const SEED: AgentSummary[] = [{
  id: 'discovery',
  name: 'Discovery Agent',
  ips: 854,
  processed: 1057000
}, {
  id: 'extraction',
  name: 'Extraction Agent',
  ips: 228,
  processed: 47481,
  confidence: 0.785
}, {
  id: 'validation',
  name: 'Validation Agent',
  ips: 191,
  processed: 40140,
  confidence: 0.893
}, {
  id: 'underwriting',
  name: 'Underwriting Agent',
  ips: 745,
  processed: 83840,
  confidence: 0.965
}, {
  id: 'audit',
  name: 'On-Chain Audit Agent',
  ips: 80,
  processed: 6842
}];
const DETAILS: Record<AgentId, AgentDetail> = {
  discovery: {
    id: 'discovery',
    sources: ['QBO', 'NetSuite', 'SAP'],
    query: 'customer.creditLimit > 10000',
    indexed: 1059421
  },
  extraction: {
    id: 'extraction',
    fieldParity: 0.785,
    duplicatesResolved: 3200,
    schemaVersion: 'v1.8'
  },
  validation: {
    id: 'validation',
    evidence: {
      erp: true,
      buyer: true,
      ship: true,
      bank: false,
      hash: true
    },
    buyerMismatch: 89,
    erpFail: 45
  },
  underwriting: {
    id: 'underwriting',
    scoreFormula: '0.39*extract + 0.25*buyer + 0.15*hist + 0.10*ship + 0.10*fingerprint',
    threshold: 0.85,
    above085: 73200
  },
  audit: {
    id: 'audit',
    verifications24h: 312,
    fingerprint: 'SHA256: 16d7e3…',
    explorerUrl: 'https://sepolia.etherscan.io/tx/0x88df016429689c079f3b2f6ad39fa052532c56795b733da78a91ebe6a713944b'
  }
};
/* =========================================================================
   RANDOMIZED ACTIVITY GENERATOR
   ========================================================================= */
const generateRecentActivity = (agentType: AgentId) => {
  const baseInvoices = [{
    prefix: 'INV-',
    base: 10231
  }, {
    prefix: 'INV-',
    base: 42751
  }, {
    prefix: 'INV-',
    base: 38492
  }, {
    prefix: 'INV-',
    base: 91823
  }, {
    prefix: 'INV-',
    base: 55617
  }, {
    prefix: 'INV-',
    base: 72384
  }, {
    prefix: 'INV-',
    base: 14926
  }, {
    prefix: 'INV-',
    base: 88531
  }, {
    prefix: 'INV-',
    base: 26749
  }, {
    prefix: 'INV-',
    base: 63218
  }];
  const statuses = {
    discovery: ['Discovered', 'Indexed', 'Qualified', 'Skipped', 'Queued'],
    extraction: ['Extracted', 'Parsed', 'Field Error', 'Validated', 'Retry'],
    validation: ['Validated', 'Failed AML', 'Passed', 'Flagged', 'Pending'],
    underwriting: ['Scored: 0.92', 'Scored: 0.71', 'Below Threshold', 'Approved', 'Manual Review'],
    audit: ['On-chain', 'Verified', 'Hash: 0x3f2...', 'Settled', 'Pending']
  };
  // Shuffle and map
  const shuffled = [...baseInvoices].sort(() => Math.random() - 0.5);
  const agentStatuses = statuses[agentType];
  return shuffled.map((inv, i) => ({
    id: `${inv.prefix}${inv.base + Math.floor(Math.random() * 1000)}`,
    action: agentStatuses[Math.floor(Math.random() * agentStatuses.length)]
  }));
};
/* =========================================================================
   PAGE
   ========================================================================= */
export function Agents() {
  const [list, setList] = useState<AgentSummary[]>(SEED);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<AgentSummary | null>(null);
  // sparkline buffers per agent id
  const lines = useRef<Record<string, number[]>>({});
  useEffect(() => {
    SEED.forEach(a => {
      if (!lines.current[a.id]) lines.current[a.id] = Array.from({
        length: 24
      }, () => a.ips);
    });
  }, []);
  // light live jitter for demo realism
  useEffect(() => {
    const clamp = (x: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, x));
    const t = setInterval(() => {
      setList(prev => prev.map(a => {
        const maxIps = a.id === 'underwriting' ? 900 : 300;
        const ips = clamp(a.ips + Math.floor(Math.random() * 15 - 7), 40, maxIps);
        const processed = a.processed + Math.floor(ips * (1 + Math.random()));
        const confidence = a.confidence != null ? clamp(a.confidence + (Math.random() * 0.01 - 0.005), 0.6, 0.99) : undefined;
        const buf = lines.current[a.id]!;
        buf.push(ips);
        if (buf.length > 24) buf.shift();
        return {
          ...a,
          ips,
          processed,
          confidence
        };
      }));
    }, 2500);
    return () => clearInterval(t);
  }, []);
  const topRowAgents = list.slice(0, 2);
  const bottomRowAgents = list.slice(2, 5);
  return <div className="w-full" data-id="element-559">
      <header className="mb-8" data-id="element-560">
        <h1 className="text-4xl font-bold text-gray-900" data-id="element-561">Agents</h1>
      </header>
      <div className="w-full space-y-5" data-id="element-562">
        {/* Top row: 2 agents */}
        <div className="grid grid-cols-2 gap-5" data-id="element-563">
          {topRowAgents.map(a => <AgentCard key={a.id} summary={a} sparkline={lines.current[a.id] || []} onOpen={() => {
          setActive(a);
          setOpen(true);
        }} data-id="element-564" />)}
        </div>
        {/* Bottom row: 3 agents */}
        <div className="grid grid-cols-3 gap-5" data-id="element-565">
          {bottomRowAgents.map(a => <AgentCard key={a.id} summary={a} sparkline={lines.current[a.id] || []} onOpen={() => {
          setActive(a);
          setOpen(true);
        }} data-id="element-566" />)}
        </div>
      </div>
      <AgentDrawer open={open} onClose={() => setOpen(false)} summary={active || undefined} data-id="element-567" />
    </div>;
}
/* =========================================================================
   CARD
   ========================================================================= */
function AgentCard({
  summary,
  sparkline,
  onOpen
}: {
  summary: AgentSummary;
  sparkline: number[];
  onOpen: () => void;
}) {
  const fmt = (n: number) => n.toLocaleString();
  return <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm hover:shadow-md hover:border-gray-400 transition-all cursor-pointer" onClick={onOpen} title="Open details" role="button" data-id="element-568">
      <div className="text-lg font-bold mb-5 text-gray-800" data-id="element-569">{summary.name}</div>
      <div className="grid grid-cols-2 gap-3 mb-5" data-id="element-570">
        <div className="bg-gray-50 rounded-md p-2.5" data-id="element-571">
          <div className="text-[10px] uppercase tracking-wide text-gray-500 font-medium mb-1" data-id="element-572">
            IPS
          </div>
          <div className="text-sm font-semibold text-gray-900" data-id="element-573">
            {summary.ips}/sec
          </div>
        </div>
        <div className="bg-gray-50 rounded-md p-2.5" data-id="element-574">
          <div className="text-[10px] uppercase tracking-wide text-gray-500 font-medium mb-1" data-id="element-575">
            Processed
          </div>
          <div className="text-sm font-semibold text-gray-900" data-id="element-576">
            {fmt(summary.processed)}
          </div>
        </div>
        <div className="bg-gray-50 rounded-md p-2.5" data-id="element-577">
          <div className="text-[10px] uppercase tracking-wide text-gray-500 font-medium mb-1" data-id="element-578">
            Confidence
          </div>
          <div className="text-sm font-semibold text-gray-900" data-id="element-579">
            {summary.confidence != null ? `${(summary.confidence * 100).toFixed(1)}%` : '—'}
          </div>
        </div>
        <div className="bg-gray-50 rounded-md p-2.5" data-id="element-580">
          <div className="text-[10px] uppercase tracking-wide text-gray-500 font-medium mb-1" data-id="element-581">
            Status
          </div>
          <div className="text-sm font-semibold text-green-600" data-id="element-582">Running</div>
        </div>
      </div>
      <Sparkline points={sparkline} data-id="element-583" />
    </div>;
}
function Sparkline({
  points
}: {
  points: number[];
}) {
  if (!points.length) return <div className="h-8 bg-gray-50 rounded" data-id="element-584" />;
  const max = Math.max(...points);
  const min = Math.min(...points);
  const norm = points.map((p, i) => {
    const x = i / Math.max(1, points.length - 1) * 100;
    const y = 100 - (p - min) / Math.max(1, max - min) * 100;
    return `${x},${y}`;
  });
  return <svg viewBox="0 0 100 30" className="h-8 w-full text-gray-400" data-id="element-585">
      <polyline fill="none" stroke="currentColor" strokeWidth="1.5" points={norm.map(p => p.replace(',', ' ')).join(' ')} data-id="element-586" />
    </svg>;
}
/* =========================================================================
   DRAWER
   ========================================================================= */
function AgentDrawer({
  open,
  onClose,
  summary
}: {
  open: boolean;
  onClose: () => void;
  summary?: AgentSummary;
}) {
  const [showSettings, setShowSettings] = useState(false);
  const [recentActivity, setRecentActivity] = useState<Array<{
    id: string;
    action: string;
  }>>([]);
  // Generate new randomized activity when drawer opens or agent changes
  useEffect(() => {
    if (open && summary) {
      setRecentActivity(generateRecentActivity(summary.id));
    }
  }, [open, summary]);
  if (!open || !summary) return null;
  const d = DETAILS[summary.id];
  const fmt = (n: number) => n.toLocaleString();
  const handleDownloadLogs = () => {
    // Generate sample log data
    const logs = [];
    const now = new Date();
    // Add header row
    logs.push(['Timestamp', 'Invoice ID', 'Action', 'Status', 'Processing Time (ms)', 'Confidence']);
    // Generate 100 sample log entries
    for (let i = 0; i < 100; i++) {
      const timestamp = new Date(now.getTime() - i * 60000).toISOString();
      const invoiceId = `INV-${58000 + i}`;
      const actions = ['Discovered', 'Extracted', 'Validated', 'Underwritten', 'Tokenized'];
      const action = actions[Math.floor(Math.random() * actions.length)];
      const status = Math.random() > 0.95 ? 'Error' : 'Success';
      const processingTime = Math.floor(Math.random() * 500) + 50;
      const confidence = summary.confidence ? (summary.confidence * 100 + (Math.random() * 10 - 5)).toFixed(1) + '%' : 'N/A';
      logs.push([timestamp, invoiceId, action, status, processingTime.toString(), confidence]);
    }
    // Convert to CSV
    const csvContent = logs.map(row => row.join(',')).join('\n');
    // Create download
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${summary.name.toLowerCase().replace(' ', '_')}_logs_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  return <>
      <div className="fixed inset-0 z-50" data-id="element-587">
        <div className="absolute inset-0 bg-black/40" onClick={onClose} data-id="element-588" />
        <aside className="absolute right-0 top-0 h-full w-[480px] bg-white shadow-xl overflow-y-auto" data-id="element-589">
          <div className="flex items-center justify-between p-5 border-b border-gray-200" data-id="element-590">
            <div className="text-sm font-semibold uppercase tracking-wide text-gray-700" data-id="element-591">
              Agent Detail
            </div>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-900 text-xl p-1" data-id="element-592">
              <X size={20} data-id="element-593" />
            </button>
          </div>
          {/* Core Metrics */}
          <Section title="Core Metrics" data-id="element-594">
            <div className="grid grid-cols-2 gap-3" data-id="element-595">
              <MetricBox label="Processed" value={fmt(summary.processed)} data-id="element-596" />
              <MetricBox label="Throughput" value={`${summary.ips}/sec`} data-id="element-597" />
              <MetricBox label="Confidence" value={summary.confidence != null ? `${(summary.confidence * 100).toFixed(1)}%` : '—'} data-id="element-598" />
              <MetricBox label="Status" value="Running" data-id="element-599" />
            </div>
          </Section>
          {/* Agent-specific content */}
          {renderSpecific(d)}
          {/* Recent Activity */}
          <Section title="Recent Activity" data-id="element-600">
            <div className="border border-gray-200 rounded-md overflow-hidden" data-id="element-601">
              {recentActivity.map((activity, i) => <div key={i} className="flex items-center justify-between px-3 py-2.5 border-b border-gray-100 last:border-b-0 text-sm" data-id="element-602">
                  <span className="font-mono text-gray-700" data-id="element-603">{activity.id}</span>
                  <span className="text-gray-600" data-id="element-604">{activity.action}</span>
                </div>)}
            </div>
          </Section>
          {/* Action Buttons */}
          <div className="flex gap-2 p-5" data-id="element-605">
            <button onClick={handleDownloadLogs} className="flex-1 px-4 py-2 text-sm font-medium border border-gray-300 rounded-md bg-white hover:bg-gray-50" data-id="element-606">
              Download Logs
            </button>
            <button onClick={() => setShowSettings(true)} className="flex-1 px-4 py-2 text-sm font-medium border border-gray-300 rounded-md bg-white hover:bg-gray-50" data-id="element-607">
              Adjust Settings
            </button>
            <button onClick={onClose} className="flex-1 px-4 py-2 text-sm font-medium rounded-md bg-gray-900 text-white hover:bg-gray-800" data-id="element-608">
              Close
            </button>
          </div>
        </aside>
      </div>
      {/* Settings Modal */}
      {showSettings && <AgentSettingsModal agent={summary} onClose={() => setShowSettings(false)} data-id="element-609" />}
    </>;
}
/* =========================================================================
   SETTINGS MODAL
   ========================================================================= */
function AgentSettingsModal({
  agent,
  onClose
}: {
  agent: AgentSummary;
  onClose: () => void;
}) {
  const [alertThreshold, setAlertThreshold] = useState(90);
  const [maxThroughput, setMaxThroughput] = useState(1000);
  const [autoRetry, setAutoRetry] = useState(true);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const handleSave = () => {
    // In a real app, this would save settings to backend
    alert('Settings saved successfully!');
    onClose();
  };
  return <div className="fixed inset-0 z-[60] flex items-center justify-center" data-id="element-610">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} data-id="element-611" />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-id="element-612">
        <div className="flex items-center justify-between mb-4" data-id="element-613">
          <h3 className="text-lg font-semibold text-gray-900" data-id="element-614">
            {agent.name} Settings
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-id="element-615">
            <X size={20} data-id="element-616" />
          </button>
        </div>
        <div className="space-y-4" data-id="element-617">
          {/* Alert Threshold */}
          <div data-id="element-618">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-619">
              Alert Threshold (%)
            </label>
            <input type="range" min="0" max="100" value={alertThreshold} onChange={e => setAlertThreshold(Number(e.target.value))} className="w-full" data-id="element-620" />
            <div className="flex justify-between text-xs text-gray-500 mt-1" data-id="element-621">
              <span data-id="element-622">0%</span>
              <span className="font-medium text-gray-900" data-id="element-623">
                {alertThreshold}%
              </span>
              <span data-id="element-624">100%</span>
            </div>
            <p className="text-xs text-gray-500 mt-1" data-id="element-625">
              Alert when confidence drops below this threshold
            </p>
          </div>
          {/* Max Throughput */}
          <div data-id="element-626">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-627">
              Max Throughput (per second)
            </label>
            <input type="number" value={maxThroughput} onChange={e => setMaxThroughput(Number(e.target.value))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900" data-id="element-628" />
            <p className="text-xs text-gray-500 mt-1" data-id="element-629">
              Maximum processing rate to prevent overload
            </p>
          </div>
          {/* Auto Retry */}
          <div className="flex items-center justify-between" data-id="element-630">
            <div data-id="element-631">
              <label className="text-sm font-medium text-gray-700" data-id="element-632">
                Auto Retry Failed Items
              </label>
              <p className="text-xs text-gray-500" data-id="element-633">
                Automatically retry failed processing attempts
              </p>
            </div>
            <button onClick={() => setAutoRetry(!autoRetry)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${autoRetry ? 'bg-gray-900' : 'bg-gray-200'}`} data-id="element-634">
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${autoRetry ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-635" />
            </button>
          </div>
          {/* Notifications */}
          <div className="flex items-center justify-between" data-id="element-636">
            <div data-id="element-637">
              <label className="text-sm font-medium text-gray-700" data-id="element-638">
                Enable Notifications
              </label>
              <p className="text-xs text-gray-500" data-id="element-639">
                Receive alerts for errors and warnings
              </p>
            </div>
            <button onClick={() => setNotificationsEnabled(!notificationsEnabled)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${notificationsEnabled ? 'bg-gray-900' : 'bg-gray-200'}`} data-id="element-640">
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notificationsEnabled ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-641" />
            </button>
          </div>
        </div>
        <div className="flex gap-3 mt-6" data-id="element-642">
          <button onClick={onClose} className="flex-1 px-4 py-2 text-sm font-medium border border-gray-300 rounded-md bg-white hover:bg-gray-50" data-id="element-643">
            Cancel
          </button>
          <button onClick={handleSave} className="flex-1 px-4 py-2 text-sm font-medium rounded-md bg-gray-900 text-white hover:bg-gray-800" data-id="element-644">
            Save Settings
          </button>
        </div>
      </div>
    </div>;
}
/* agent-specific panels */
function renderSpecific(d: AgentDetail) {
  switch (d.id) {
    case 'discovery':
      return <Section title="Discovery Configuration" data-id="element-645">
          <div className="grid grid-cols-2 gap-3" data-id="element-646">
            <div className="col-span-2 bg-gray-50 border border-gray-200 p-3 rounded-md" data-id="element-647">
              <div className="text-[10px] uppercase text-gray-500 mb-1" data-id="element-648">
                Sources
              </div>
              <div className="text-sm font-semibold text-gray-900" data-id="element-649">
                {d.sources.join(' • ')}
              </div>
            </div>
            <div className="col-span-2 bg-gray-50 border border-gray-200 p-3 rounded-md" data-id="element-650">
              <div className="text-[10px] uppercase text-gray-500 mb-1" data-id="element-651">
                Query
              </div>
              <div className="text-xs font-mono text-gray-900" data-id="element-652">{d.query}</div>
            </div>
            <MetricBox label="Indexed" value={d.indexed.toLocaleString()} data-id="element-653" />
            <MetricBox label="Extraction Rate" value="1% (Target)" data-id="element-654" />
          </div>
        </Section>;
    case 'extraction':
      return <Section title="Extraction Quality" data-id="element-655">
          <div className="grid grid-cols-2 gap-3" data-id="element-656">
            <MetricBox label="Field Parity" value={`${(d.fieldParity * 100).toFixed(1)}%`} data-id="element-657" />
            <MetricBox label="Duplicates Resolved" value={d.duplicatesResolved.toLocaleString()} data-id="element-658" />
            <MetricBox label="Schema Version" value={d.schemaVersion} data-id="element-659" />
            <MetricBox label="Cost per Invoice" value="$0.0055" data-id="element-660" />
          </div>
        </Section>;
    case 'validation':
      return <Section title="Evidence Checks" data-id="element-661">
          <div className="flex flex-wrap gap-2 mb-4" data-id="element-662">
            <EvidencePill label="ERP" pass={d.evidence.erp} data-id="element-663" />
            <EvidencePill label="BUYER" pass={d.evidence.buyer} data-id="element-664" />
            <EvidencePill label="SHIPPING" pass={d.evidence.ship} data-id="element-665" />
            <EvidencePill label="BANK" pass={d.evidence.bank} data-id="element-666" />
            <EvidencePill label="HASH" pass={d.evidence.hash} data-id="element-667" />
          </div>
          <div className="grid grid-cols-2 gap-3" data-id="element-668">
            <MetricBox label="Buyer Mismatch" value={String(d.buyerMismatch)} data-id="element-669" />
            <MetricBox label="ERP Fails" value={String(d.erpFail)} data-id="element-670" />
          </div>
        </Section>;
    case 'underwriting':
      return <Section title="Underwriting Model" data-id="element-671">
          <div className="bg-gray-50 border border-gray-200 p-3 rounded-md mb-4" data-id="element-672">
            <div className="text-[10px] uppercase text-gray-500 mb-2" data-id="element-673">
              Score Formula
            </div>
            <div className="text-xs font-mono text-gray-700 leading-relaxed" data-id="element-674">
              {d.scoreFormula}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3" data-id="element-675">
            <MetricBox label="Threshold" value={String(d.threshold)} data-id="element-676" />
            <MetricBox label="Above Threshold" value={d.above085.toLocaleString()} data-id="element-677" />
          </div>
        </Section>;
    case 'audit':
      return <Section title="On-Chain Verification" data-id="element-678">
          <div className="grid grid-cols-2 gap-3 mb-3" data-id="element-679">
            <MetricBox label="Verifications (24h)" value={String(d.verifications24h)} data-id="element-680" />
            <div className="bg-gray-50 border border-gray-200 p-3 rounded-md" data-id="element-681">
              <div className="text-[10px] uppercase text-gray-500 mb-1" data-id="element-682">
                Fingerprint
              </div>
              <div className="text-xs font-mono text-gray-900" data-id="element-683">
                {d.fingerprint}
              </div>
            </div>
          </div>
          <a href={d.explorerUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900" data-id="element-684">
            <ExternalLink size={14} data-id="element-685" />
            View on Etherscan
          </a>
        </Section>;
  }
}
/* primitives */
function Section({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <section className="p-5 border-b border-gray-100" data-id="element-686">
      <div className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-4" data-id="element-687">
        {title}
      </div>
      {children}
    </section>;
}
function MetricBox({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="bg-gray-50 border border-gray-200 p-3 rounded-md" data-id="element-688">
      <div className="text-[10px] uppercase text-gray-500 mb-1" data-id="element-689">{label}</div>
      <div className="text-sm font-semibold text-gray-900" data-id="element-690">{value}</div>
    </div>;
}
function EvidencePill({
  label,
  pass
}: {
  label: string;
  pass: boolean;
}) {
  return <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-[11px] font-medium border ${pass ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`} data-id="element-691">
      {label} {pass ? 'PASS' : 'FAIL'}
    </span>;
}